import { cn } from "@workspace/ui/lib/utils";
import { type CSSProperties, forwardRef, type ReactNode, useId } from "react";

type LiquidGlassVariant = "default" | "landing";

type LiquidGlassProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  blurAmount?: number;
  saturation?: number;
  cornerRadius?: number;
  padding?: string;
  variant?: LiquidGlassVariant;
  /** Background opacity 0-100. Default is 12 for dark mode aesthetic. */
  opacity?: number;
};

export const LiquidGlass = forwardRef<HTMLDivElement, LiquidGlassProps>(
  (
    {
      children,
      className = "",
      style,
      blurAmount = 16,
      saturation = 140,
      cornerRadius = 20,
      padding = "12px 16px",
      variant = "default",
      opacity,
    },
    ref
  ) => {
    const filterId = useId();
    const isLanding = variant === "landing";
    const hasCustomOpacity = opacity !== undefined;

    return (
      <div
        className={`relative ${className}`}
        ref={ref}
        style={{
          borderRadius: cornerRadius,
          ...style,
        }}
      >
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          height="100%"
          width="100%"
        >
          <defs>
            <filter
              colorInterpolationFilters="sRGB"
              height="130%"
              id={filterId}
              width="130%"
              x="-15%"
              y="-15%"
            >
              <feGaussianBlur
                in="SourceGraphic"
                result="blur"
                stdDeviation="2"
              />
              <feColorMatrix
                in="blur"
                result="saturated"
                type="saturate"
                values={String(saturation / 100)}
              />
            </filter>
          </defs>
        </svg>

        <div
          className={cn(
            !hasCustomOpacity && "glass-backdrop",
            isLanding && "glass-backdrop-landing"
          )}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: cornerRadius,
            backdropFilter: `blur(${blurAmount}px) saturate(${saturation}%)`,
            WebkitBackdropFilter: `blur(${blurAmount}px) saturate(${saturation}%)`,
            ...(hasCustomOpacity && {
              background: `linear-gradient(135deg, rgba(255,255,255,${opacity / 100}) 0%, rgba(255,255,255,${(opacity * 0.6) / 100}) 100%)`,
              boxShadow: `
                inset 0 0.5px 0 0 rgba(255,255,255,0.25),
                inset 0 1px 2px 0 rgba(255,255,255,0.1),
                0 8px 32px -8px rgba(0,0,0,0.35),
                0 2px 8px -2px rgba(0,0,0,0.2)
              `,
              border: `1px solid rgba(255,255,255,${opacity / 100})`,
            }),
          }}
        />

        <div
          className="glass-specular pointer-events-none"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: cornerRadius,
            background:
              "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(255,255,255,0.15) 0%, transparent 60%)",
            mixBlendMode: "overlay",
          }}
        />

        <div
          className="glass-content relative"
          style={{
            padding,
            zIndex: 1,
          }}
        >
          {children}
        </div>
      </div>
    );
  }
);

LiquidGlass.displayName = "LiquidGlass";
